import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { ADDRESS_SUBFIELDS, type SapAddressPart } from '@hcc/types';
import type { SapFormField } from './mappingTypes';

/**
 * Searchable field picker for the mapping editor, following the
 * PersonPicker combobox pattern. Forms can carry 60+ questions, so a
 * native select is unusable: this groups fields under their form page
 * and filters as you type. The popover matches the trigger width, so
 * it can never escape the card the way the native dropdown did.
 *
 * Composite Address fields store one object; the SAP side wants the
 * parts separately. Targets that declare an addressPart accept the
 * whole Address field and the resolver extracts that part, so the
 * picker labels Address entries with the part they will supply.
 * Targets without an addressPart can never read a composite, so
 * Address fields are hidden for them.
 */
export function FieldPicker({
  fields,
  value,
  disabled,
  addressPart,
  onChange,
}: {
  fields: SapFormField[];
  value: string | null;
  disabled?: boolean;
  addressPart?: SapAddressPart | null;
  onChange: (fieldKey: string | null) => void;
}) {
  const partLabel = addressPart
    ? ADDRESS_SUBFIELDS.find((s) => s.key === addressPart)?.label
    : undefined;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuId = useId();

  const selected = fields.find((f) => f.key === value) ?? null;

  const groups = useMemo(() => {
    const usable = addressPart ? fields : fields.filter((f) => f.type !== 'address');
    const needle = q.trim().toLowerCase();
    const matches = needle
      ? usable.filter(
          (f) =>
            f.label.toLowerCase().includes(needle) || f.key.toLowerCase().includes(needle),
        )
      : usable;
    const byPage = new Map<string, SapFormField[]>();
    for (const f of matches) {
      const page = f.page || 'Other';
      byPage.set(page, [...(byPage.get(page) ?? []), f]);
    }
    return [...byPage.entries()];
  }, [fields, q, addressPart]);

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function pick(f: SapFormField) {
    onChange(f.key);
    setOpen(false);
    setQ('');
  }

  function clear(e: React.MouseEvent) {
    e.stopPropagation();
    onChange(null);
    setQ('');
  }

  return (
    <div ref={rootRef} className="relative min-w-0">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
        disabled={disabled}
        onClick={() => {
          setQ('');
          setOpen((v) => !v);
        }}
        className={[
          'flex h-9 w-full items-center gap-2 rounded-token-sm border border-line/20 bg-surface px-2.5 text-left text-sm transition-colors',
          'hover:bg-surface-2',
          'focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-strong focus-visible:ring-offset-1',
          'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-fg-muted',
        ].join(' ')}
      >
        {selected ? (
          <>
            <span className="min-w-0 flex-1 truncate">
              {selected.label}
              <span className="ml-2 text-xs text-fg-dim">
                {selected.type === 'address' && partLabel ? `uses ${partLabel}` : selected.type}
              </span>
            </span>
            {!disabled ? (
              <span
                role="button"
                tabIndex={-1}
                aria-label={`Clear ${selected.label}`}
                onClick={clear}
                className="rounded p-0.5 text-fg-dim hover:bg-surface-2 hover:text-fg-muted"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            ) : null}
          </>
        ) : (
          <span className="flex-1 truncate text-fg-dim">Choose field…</span>
        )}
        <ChevronDown
          aria-hidden
          className={`h-4 w-4 shrink-0 text-fg-muted transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open ? (
        <div
          id={menuId}
          role="listbox"
          className="absolute left-0 right-0 z-50 mt-1 overflow-hidden rounded-token-sm bg-surface shadow-token-lg ring-1 ring-line/15"
        >
          <div className="border-b border-line/10 p-2">
            <div className="relative">
              <Search
                aria-hidden
                className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-dim"
              />
              <input
                ref={inputRef}
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search fields…"
                className="block h-8 w-full rounded-token-sm border border-line/20 bg-surface pl-8 pr-2 text-sm focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-strong"
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {groups.length === 0 ? (
              <div className="px-3 py-3 text-xs text-fg-muted">No matching fields.</div>
            ) : (
              groups.map(([page, pageFields]) => (
                <div key={page}>
                  <div className="sticky top-0 bg-surface-2 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
                    {page}
                  </div>
                  <ul>
                    {pageFields.map((f) => (
                      <li key={f.key}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={f.key === value}
                          onClick={() => pick(f)}
                          className={[
                            'flex w-full items-baseline gap-2 px-3 py-1.5 text-left text-sm transition-colors',
                            f.key === value ? 'bg-accent/10 text-accent' : 'hover:bg-surface-2',
                          ].join(' ')}
                        >
                          <span className="min-w-0 flex-1 truncate">{f.label}</span>
                          <span className="shrink-0 text-[11px] text-fg-dim">
                            {f.type === 'address' && partLabel ? `uses ${partLabel}` : f.type}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
